import type { FilterFn } from '@tanstack/vue-table';

/**
 * Status filter function for boolean/numeric status values
 * Filters rows where the cell value is included in the filter value array
 */
export const booleanFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
    if (!filterValue || filterValue.length === 0) {
        return true;
    }
    const cellValue = row.getValue(columnId);
    return filterValue.includes(cellValue);
};

export const valueInArrayFilterFn: FilterFn<any> = (
    row,
    columnId: string,
    filterValue: number[],
) => {
    return filterValue.includes(row.getValue<number>(columnId));
};

/**
 * Date range filter function for filtering dates within a range or from a date forward
 * FilterValue formats:
 * - Range: "DD.MM.YYYY,DD.MM.YYYY" or "DD/MM/YYYY,DD/MM/YYYY" or "DD-MM-YYYY,DD-MM-YYYY"
 * - From only: "DD.MM.YYYY" or "DD/MM/YYYY" or "DD-MM-YYYY"
 * Cell value format: "DD.MM.YYYY" or "DD/MM/YYYY" or "DD-MM-YYYY"
 */
export const dateRangeFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
    if (!filterValue || typeof filterValue !== 'string') {
        return true;
    }

    const cellValue = row.getValue(columnId);
    if (!cellValue || typeof cellValue !== 'string') {
        return true;
    }

    const parseDate = (dateStr: string): Date | null => {
        // Handle all three formats: DD.MM.YYYY, DD/MM/YYYY, DD-MM-YYYY
        const cleanStr = dateStr.trim();
        if (!cleanStr) return null;

        // Check for ISO format first
        if (/^\d{4}-\d{2}-\d{2}/.test(cleanStr)) {
            const d = new Date(cleanStr);
            return isNaN(d.getTime()) ? null : d;
        }

        let day: number, month: number, year: number;

        if (cleanStr.includes('.')) {
            [day, month, year] = cleanStr.split('.').map(Number);
        } else if (cleanStr.includes('/')) {
            [day, month, year] = cleanStr.split('/').map(Number);
        } else if (cleanStr.includes('-')) {
            [day, month, year] = cleanStr.split('-').map(Number);
        } else {
            return null;
        }

        if (isNaN(day) || isNaN(month) || isNaN(year)) {
            return null;
        }

        return new Date(year, month - 1, day); // month is 0-based in Date constructor
    };

    const cellDate = parseDate(cellValue);
    if (!cellDate) {
        return true;
    }

    // Check if it's a range (contains comma)
    if (filterValue.includes(',')) {
        // Split into from and to parts
        const parts = filterValue.split(',');

        // Handle "from" date if present
        const startDateStr = parts[0].trim();
        const startDate = startDateStr ? parseDate(startDateStr) : null;

        // Handle "to" date if present
        const endDateStr = parts.length > 1 ? parts[1].trim() : '';
        const endDate = endDateStr ? parseDate(endDateStr) : null;

        // If neither date is valid, don't filter
        if (!startDate && !endDate) {
            return true;
        }

        // If only start date is provided
        if (startDate && !endDate) {
            return cellDate >= startDate;
        }

        // If only end date is provided
        if (!startDate && endDate) {
            return cellDate <= endDate;
        }

        return cellDate >= startDate! && cellDate <= endDate!;
    } else {
        const fromDate = parseDate(filterValue);

        if (!fromDate) {
            return true;
        }

        return cellDate >= fromDate;
    }
};

/**
 * Filters rows where a period range (two dates) overlaps with the selected range.
 * - Cell value expected: string formatted like "DD.MM.YYYY - DD.MM.YYYY" (spaces optional),
 *   or ISO dates in two separate columns bound via accessorFn.
 * - Filter value: same formats as dateRangeFilterFn (single from or from,to)
 */
export const periodRangeFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
    if (!filterValue || typeof filterValue !== 'string') {
        return true;
    }

    const cellValue = row.getValue(columnId);

    // Try to read from underlying original if provided
    let periodFrom: string | undefined;
    let periodTo: string | undefined;

    if (row.original?.settlement_period_from && row.original?.settlement_period_to) {
        periodFrom = row.original.settlement_period_from; // ISO
        periodTo = row.original.settlement_period_to; // ISO
    } else if (typeof cellValue === 'string') {
        const m = cellValue.match(/(\d{2}[./-]\d{2}[./-]\d{4})\s*-\s*(\d{2}[./-]\d{2}[./-]\d{4})/);
        if (m) {
            periodFrom = m[1];
            periodTo = m[2];
        }
    }

    const parse = (v: string | undefined): Date | null => {
        if (!v) return null;
        if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
        }
        const s = v.replaceAll('/', '.').replaceAll('-', '.');
        const [d, m, y] = s.split('.').map(Number);
        if ([d, m, y].some(isNaN)) return null;
        return new Date(y, m - 1, d);
    };

    const fromDate = parse(periodFrom);
    const toDate = parse(periodTo);
    if (!fromDate || !toDate) {
        return true;
    }

    // Parse filter value (supports "from" or "from,to")
    const parts = filterValue.split(',');
    const filterFrom = parts[0] ? parse(parts[0].trim()) : null;
    const filterTo = parts.length > 1 && parts[1] ? parse(parts[1].trim()) : null;

    if (!filterFrom && !filterTo) {
        return true;
    }

    // If only filterFrom: include any rows where periodTo >= filterFrom
    if (filterFrom && !filterTo) {
        return toDate >= filterFrom;
    }

    // If only filterTo: include any rows where periodFrom <= filterTo
    if (!filterFrom && filterTo) {
        return fromDate <= filterTo;
    }

    // Otherwise, overlap check: (from <= filterTo) && (to >= filterFrom)
    return fromDate <= (filterTo as Date) && toDate >= (filterFrom as Date);
};

valueInArrayFilterFn.autoRemove = (val: any) => testFalsey(val) || !val.length;

function testFalsey(val: any) {
    return val === undefined || val === null || val === '';
}
